import { useEffect, useState, type ReactNode } from 'react';
import type { ClientEvent, ClientSnapshot } from '@lw/client-core';
import { useRuntime } from '../runtime/useRuntime.js';
import { fmtTime } from '../app/helpers.js';
import { devTag } from '../runtime/identity.js';

const EVENT_TEXT: Record<string, (p: Record<string, unknown>) => string> = {
  SEAT_TAKEN: (p) => (p['claim'] ? `${String(p['role'])} seat claimed` : `${String(p['role'])} control taken`),
  SEAT_RELEASED: (p) => `${String(p['role'])} seat released`,
  CFF_CREATED: (p) => `Call for fire — ${String((p['target'] as { value: string }).value)}`,
  MISSION_STARTED: (p) => `Fire mission confirmed, estimate ${String(p['expectedMin'])} min`,
  SOLUTION_ENTERED: (p) => `Solution: code ${String(p['laserCode'])}, flight ${String(p['shellFlightTimeS'])} s, laser ${String(p['laserOnLeadS'])} s`,
  PROGRAMMING_DONE: () => 'Shell programmed',
  LOADING_DONE: () => 'Gun loaded — WEAPON READY',
  UAV_READY_SET: (p) => `UAV READY (audio ${String(p['audioSelfTest'])})`,
  UAV_READY_RETRACTED: () => 'UAV not ready',
  STEP_REVERTED: (p) => `Step reversed → ${String(p['to'])}`,
  FIRED: () => 'SHOT — T0 committed, countdown started',
  FIRE_HELD_FOR_REVIEW: (p) => `Late fire held for review (${String(p['reason'])})`,
  FIRE_CANCELLED: (p) => `FIRE CANCELLED (${String(p['band'])})`,
  LATE_CANCEL_REJECTED: () => 'Cancel arrived after impact — no change',
  CEASE_LASING_ACK: () => 'CEASE LASING acknowledged',
  LASER_ON_CONFIRMED: () => 'LASER ON confirmed',
  LASER_OFF_CONFIRMED: () => 'LASER OFF',
  LASER_OFF_TIMEOUT: () => 'Laser off unconfirmed (30 s)',
  CUE_LASER_WARN: () => 'Laser warn window',
  CUE_LASER_ON: () => 'LASER ON',
  CUE_IMPACT_WARN: () => 'Impact warn window',
  CUE_IMPACT: () => 'IMPACT',
  AAR_OPENED: () => 'Save / discard offered',
  MISSION_SAVED: () => 'Mission saved',
  MISSION_AUTO_SAVED: (p) => `Mission auto-saved (${String(p['reason'])})`,
  MISSION_DISCARDED: () => 'Mission discarded',
  MISSION_FORCE_ENDED: () => 'Mission ended by admin',
  SERVER_CLOCK_DISCONTINUITY: (p) => `Server clock discontinuity ${String(p['deltaMs'])} ms`,
  AUTHZ_VIOLATION: (p) => `Unauthorised ${String(p['cmd'])} rejected`,
};

function eventClass(e: ClientEvent): string {
  if (e.type === 'FIRED' || e.type === 'CUE_IMPACT' || e.type === 'FIRE_CANCELLED') return 'bad';
  if (e.type.startsWith('LASER_') || e.type === 'MISSION_SAVED' || e.type === 'UAV_READY_SET') return 'good';
  if (e.delayMs > 2000 || e.type.includes('REVIEW') || e.type.includes('TIMEOUT') || e.type === 'STEP_REVERTED') return 'warn';
  return '';
}

export function EventList({ events, log }: { events: readonly ClientEvent[]; log: { at: number; text: string; cls: string }[] }): ReactNode {
  const rows = [
    ...events.map((e) => ({ t: e.tEvent, m: (EVENT_TEXT[e.type] ?? ((p) => `${e.type} ${JSON.stringify(p)}`))(e.payload) + (e.delayMs > 2000 ? ` [late +${(e.delayMs / 1000).toFixed(1)} s]` : ''), who: e.actorRole === 'SERVER' ? 'server' : `${e.actorUser ?? '?'}@${devTag((e as { actorDevice?: string | null }).actorDevice)}`, cls: eventClass(e), k: `e${e.seq}` })),
    ...log.map((l, i) => ({ t: l.at, m: l.text, who: 'this device', cls: l.cls, k: `l${i}` })),
  ]
    .sort((a, b) => b.t - a.t)
    .slice(0, 60);
  if (!rows.length) return <div className="muted" style={{ fontSize: 12 }}>No events yet.</div>;
  return (
    <>
      {rows.map((r) => (
        <div key={r.k} className={`ev ${r.cls}`}>
          <span className="t">{fmtTime(r.t, true)}</span>
          <span>
            <span className="m">{r.m}</span>
            <br />
            <span className="who">{r.who}</span>
          </span>
        </div>
      ))}
    </>
  );
}

export function Ticker(): ReactNode {
  const { state } = useRuntime();
  return (
    <aside className="ticker">
      <h3>Events</h3>
      <EventList events={state.events} log={state.log} />
    </aside>
  );
}

export function TickerMobile(): ReactNode {
  const { state } = useRuntime();
  return (
    <details className="ticker-mobile">
      <summary>Events ({state.events.length})</summary>
      <EventList events={state.events} log={state.log} />
    </details>
  );
}

/** §6.4 CANCEL → CONFIRM dialog; KEEP FIRING is the default; auto-dismiss 5 s; pressable while RED. */
export function CancelDialog({ check, onConfirm, onClose }: { check: boolean; onConfirm: () => void; onClose: () => void }): ReactNode {
  const { rt } = useRuntime();
  const [left, setLeft] = useState(5);
  useEffect(() => {
    const t = setInterval(() => setLeft((n) => n - 1), 1000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    if (left <= 0) onClose();
  }, [left, onClose]);
  const red = rt.client.indicator.current().colour === 'RED';
  return (
    <div className="modal-bg">
      <div className="modal danger" role="dialog" aria-modal="true" aria-label="Confirm cancel">
        <h2>{check ? 'Check fire?' : 'Cancel the fire?'}</h2>
        <p>
          {check ? 'The laser window is open. The UAV will be told to cease lasing.' : 'Use this only if SHOT was pressed by mistake. Everyone returns to WEAPON READY and the UAV must confirm readiness again.'}
          {red ? ' Your link is down: the cancel will be queued and judged when it arrives.' : ''}
        </p>
        <div className="actions">
          <button className="btn confirm" onClick={onClose} autoFocus>Keep firing</button>
          <button className="btn danger" onClick={onConfirm} data-testid="confirm-cancel">Confirm {check ? 'check fire' : 'cancel'}</button>
        </div>
        <p className="muted" style={{ fontSize: 12 }}>Closes by itself in {Math.max(0, left)} s.</p>
      </div>
    </div>
  );
}

export function ReviewCard({ snapshot, forFdc }: { snapshot: ClientSnapshot; forFdc: boolean }): ReactNode {
  const r = snapshot.review;
  const sol = snapshot.solution;
  if (!r || !sol) return null;
  const meta = (r as unknown as { t0Meta?: { candidates: { ageMono: number | null; ageWall: number | null; claim: number | null }; uncMs: number } }).t0Meta;
  const age = r.tsRx - r.t0;
  const impactAgo = r.tsRx - r.tImpact;
  return (
    <div className="card red" data-testid="review">
      <div className="eyebrow">Late fire report · {r.reason.replace(/_/g, ' ').toLowerCase()}</div>
      <h2 className="big">{forFdc ? 'Fire arrived late' : 'FDC reviewing'}</h2>
      <p>
        The SHOT press reached the server <b className="mono">{(age / 1000).toFixed(1)} s</b> after the press.{' '}
        {impactAgo > 0 ? <>The reconstructed impact was <b className="mono">{(impactAgo / 1000).toFixed(0)} s ago</b>, so no countdown is shown.</> : <>Impact would be in <b className="mono">{(-impactAgo / 1000).toFixed(1)} s</b> — too close to arm cues unreviewed.</>}
      </p>
      <dl className="kv">
        <dt>Received</dt><dd>{fmtTime(r.tsRx, true)}</dd>
        {meta ? (
          <>
            <dt>Age-based (monotonic)</dt><dd>{fmtTime(meta.candidates.ageMono, true)}</dd>
            <dt>Age-based (wall clock)</dt><dd>{fmtTime(meta.candidates.ageWall, true)}</dd>
            <dt>Client claim</dt><dd>{fmtTime(meta.candidates.claim, true)}</dd>
          </>
        ) : null}
        <dt>Reconstructed T0</dt><dd>{fmtTime(r.t0, true)}{meta ? ` (±${meta.uncMs} ms)` : ''}</dd>
        <dt>Laser on</dt><dd>{fmtTime(r.tLaserOn, true)}</dd>
        <dt>Impact</dt><dd>{fmtTime(r.tImpact, true)}</dd>
      </dl>
    </div>
  );
}
